import { ChatBubbleOvalLeftIcon, FlagIcon, PencilIcon, UserIcon } from "@heroicons/react/24/solid";
import { Avatar } from "@moe/oss/ui/avatar";
import { Button } from "@moe/oss/ui/button";
import { isoToFriendly } from "@moe/oss/utils/time";
import { Character } from "@moe/priv/model/character";
import { Link, useMatchRoute } from "@tanstack/react-router";
import { useCopyToClipboard } from "@uidotdev/usehooks";
import { CardActionBar } from "@web/components/CardActionBar";
import { CreatorLink } from "@web/components/CreatorLink";
import { ReportModal } from "@web/components/ReportModal";
import { TagChip } from "@web/components/tag/TagChip";
import { useCreateChat } from "@web/hooks/use-create-chat";
import { useLikeChar } from "@web/hooks/use-like-char";
import { useTagNavigation } from "@web/route-services/index/use-tag-navigation";
import { useAppContext } from "@web/route-services/root/AppContext";
import { useCallback } from "react";
import { toast } from "sonner";
import { AuthModal } from "./AuthModal";

interface Props {
  char: Character;
}

export function CharacterCard({ char }: Props) {
  const createChat = useCreateChat();
  const { isDebug, closeModal } = useAppContext();
  const { doFocusExploreRef, createModal, profile, session } = useAppContext();
  const matchRoute = useMatchRoute();
  const isCharacterRoute = matchRoute({ to: "/character/$characterId" });

  const handleChatClick = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      // If it's a right click or middle click, let the browser handle it naturally
      if (e.button === 2 || e.button === 1) return;

      // Prevent default only for left clicks to handle with our custom logic
      e.preventDefault();
      await createChat(char);
    },
    [createChat, char]
  );

  const { likeCount, isLiked, like: handleLike } = useLikeChar({ char: char });
  const handleChat = useCreateChat();
  const [_, copyToClipboard] = useCopyToClipboard();

  const handleShare = async () => {
    await copyToClipboard(`${window.location.origin}/character/${char.id}`);
    toast("Share link copied!");
  };

  const { only } = useTagNavigation();
  const handleTagSelect = (tag: string) => {
    doFocusExploreRef.current = true;
    only(tag);
    closeModal();
  };

  const handleReportClick = () => {
    if (!session) {
      createModal({ content: <AuthModal formKind="signup" /> });
      return;
    }
    createModal({ content: <ReportModal characterID={char.id} /> });
  };

  return (
    <div className="bg-background text-tx-primary h-[80vh] w-full overflow-hidden rounded-3xl pt-4 md:h-[45rem] md:w-[32rem]">
      <div className="scroll-secondary-thin h-full overflow-y-auto px-5 pb-8 md:px-9">
        {/* Avatar section */}
        <div className="rounded-5xl relative mb-3 w-full">
          <Avatar className="aspect-square h-auto w-full rounded-3xl shadow-lg" src={char.avatar} />
          <CardActionBar
            size="md"
            className="right-5 top-0 rounded-b-2xl px-2 pb-4 pt-8"
            like={{
              likeCount,
              isLiked,
              onLike: handleLike
            }}
            share={{
              onShare: handleShare
            }}
            chat={{
              messageCount: char.messageCount,
              onChat: () => handleChat(char)
            }}
          />

          {/* Copy ID Button For Debugging */}
          {isDebug && (
            <button
              className="absolute left-2 top-3 z-10 p-2 font-semibold text-blue-600 opacity-0 hover:opacity-100"
              onClick={() => {
                navigator.clipboard.writeText(char.id.toString());
              }}
            >
              Copy ID
            </button>
          )}
        </div>

        {/* Horizontal Character Action Bar */}
        <div className="scroll-primary mb-8 flex flex-row space-x-2 overflow-x-auto py-2">
          <Button onClick={handleChatClick}>
            <ChatBubbleOvalLeftIcon />
            Chat
          </Button>
          {profile?.username === char.createdBy && (
            <Link to={"/studio/update"} search={{ cardID: char.id }}>
              <Button
                variant="clear"
                onClick={() => {
                  closeModal();
                }}
              >
                <PencilIcon />
                Edit
              </Button>
            </Link>
          )}
          <Link to={`/character/${char.id}`}>
            <Button variant="clear" className="">
              <UserIcon />
              Page
            </Button>
          </Link>
          <Button variant="clear" className="shrink-0" onClick={handleReportClick}>
            <FlagIcon />
            Report
          </Button>
        </div>

        {/* Rest of the content remains the same */}
        <div className="mb-8">
          <h2 className="text-tx-primary  line-clamp-2 w-full text-ellipsis text-xl font-bold">{char.title}</h2>
          <CreatorLink createdBy={char?.createdBy} externalCreatedBy={char?.externalCreatedBy} />
          <div className="text-tx-tertiary mt-1 text-sm font-semibold">
            <span>Created: {isoToFriendly(char.createdAt)}</span>
            {char.updatedAt && <span className="ml-2">Updated: {isoToFriendly(char.updatedAt)}</span>}
          </div>
        </div>

        <div className="flex flex-col space-y-4 md:pt-4">
          <div>
            <h3 className="text-tx-secondary text-lg font-semibold">Tagline</h3>
            <p className="whitespace-pre-wrap text-base font-medium text-neutral-300">{char.tagline}</p>
          </div>
          <div>
            <h3 className="text-tx-secondary  text-lg font-semibold">Greeting</h3>
            <p className="whitespace-pre-wrap text-base font-medium text-neutral-300">{char.greeting}</p>
          </div>
          <h3 className="text-tx-secondary text-lg font-semibold">Tags</h3>
          <div className="mt-2 flex flex-wrap gap-1">
            {char.tags?.map((tag, index) => <TagChip key={index} tag={tag} onSelect={handleTagSelect} />)}
          </div>
        </div>
      </div>
    </div>
  );
}
